import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Home, Briefcase, Car, CreditCard, FileText, User, CheckCircle } from "lucide-react";
import LoansHero from "../components/LoansHero";
import { DASHBOARD_URL } from "../config/urls";
import "../styles/loans.css";

/* ========= DATA (EXACT CONTENT) ========= */

const loanServices = [
  {
    title: "Unsecured Business Loan",
    desc: "Get quick access to funds without collateral to support your business growth.",
    icon: Briefcase,
    features: ["No collateral required", "Up to ₹50 Lakhs", "24-48 hour approval"],
    cta: "apply"
  },
  {
    title: "Personal Loan",
    desc: "Meet your personal financial needs with hassle-free loan approvals.",
    icon: User,
    features: ["Flexible tenure", "Competitive rates", "Minimal documentation"],
    cta: "apply"
  },
  {
    title: "Home Loan",
    desc: "Make your dream home a reality with flexible financing options.",
    icon: Home,
    features: ["Up to 30 years tenure", "Low interest rates", "Tax benefits"],
    cta: "apply"
  },
  {
    title: "Loan Against Property",
    desc: "Unlock the value of your property to access substantial funding.",
    icon: Home,
    features: ["High loan amount", "Longer repayment", "Lower interest rates"],
    cta: "apply"
  },
  {
    title: "SME Loan",
    desc: "Tailored financial solutions to empower small and medium enterprises.",
    icon: Briefcase,
    features: ["Customized solutions", "Quick processing", "Flexible terms"],
    cta: "apply"
  },
  {
    title: "CC/OD Limit",
    desc: "Manage your business cash flow efficiently with a credit line or overdraft facility.",
    icon: CreditCard,
    features: ["Interest on used amount", "Flexible repayment", "Easy renewal"],
    cta: "apply"
  },
  {
    title: "Credit Card",
    desc: "Enjoy financial freedom with cards offering exclusive benefits and rewards.",
    icon: CreditCard,
    features: ["Reward points", "Zero liability", "Contactless payments"],
    cta: "apply"
  },
  {
    title: "Used Car Loan",
    desc: "Drive your dream car with easy financing for pre-owned vehicles.",
    icon: Car,
    features: ["Up to 100% funding", "Quick approval", "Competitive rates"],
    cta: "apply"
  },
];

const complianceServices = [
  {
    title: "Audit Services",
    desc: "Ensure compliance and financial accuracy with expert audit services.",
    icon: FileText,
    features: ["Statutory audit", "Tax audit", "Internal audit"]
  },
  {
    title: "GST Services",
    desc: "End-to-end GST compliance, filing, and consultation services.",
    icon: FileText,
    features: ["GST registration", "Return filing", "Refund processing"]
  },
  {
    title: "Income Tax",
    desc: "Simplify your tax filing process with professional assistance.",
    icon: FileText,
    features: ["ITR filing", "Tax planning", "TDS compliance"]
  },
  {
    title: "Company Formation",
    desc: "Start your business effortlessly with our company registration services.",
    icon: Briefcase,
    features: ["Private limited", "LLP", "OPC"]
  },
  {
    title: "Project Reports",
    desc: "Professionally crafted project reports to support your loan applications.",
    icon: FileText,
    features: ["Bank loan reports", "Feasibility studies", "Financial projections"]
  }
];

/* ========= REUSABLE CARD ========= */

const ServiceCard = ({ title, desc, icon: Icon, features = [], cta = "learn", link }) => {
  return (
    <div className="service-card">
      <div className="card-icon">
        {Icon && <Icon size={24} />}
      </div>
      <h3>{title}</h3>
      <p>{desc}</p>
      
      {features && features.length > 0 && (
        <ul className="card-features">
          {features.slice(0, 3).map((feature, i) => (
            <li key={i}>
              <CheckCircle size={14} />
              {feature}
            </li>
          ))}
        </ul>
      )}
      
      <div className="card-cta">
        {cta === "apply" ? (
          <a href={link} className="apply-btn">Apply Now</a>
        ) : (
          <Link to="#" className="learn-btn">
            Learn more <span>→</span>
          </Link>
        )}
      </div>
    </div>
  );
}

/* ========= PAGE ========= */

function Loans() {
  const [activeTab, setActiveTab] = useState('loans');
  
  return (
    <main className="loans-page">
      {/* Hero Section */}
      <LoansHero />
      
      {/* Services Tabs */}
      <section className="services-tabs">
        <div className="container">
          <div className="tabs-header">
            <button 
              className={`tab-btn ${activeTab === 'loans' ? 'active' : ''}`}
              onClick={() => setActiveTab('loans')}
            >
              Loan Products
            </button>
            <button 
              className={`tab-btn ${activeTab === 'compliance' ? 'active' : ''}`}
              onClick={() => setActiveTab('compliance')}
            >
              Compliance Services
            </button>
          </div>
          
          <div className="tab-content">
            {activeTab === 'loans' ? (
              <div className="services-grid">
                {loanServices.map((service, index) => (
                  <ServiceCard
                    key={`loan-${index}`}
                    title={service.title}
                    desc={service.desc}
                    icon={service.icon}
                    features={service.features}
                    cta={service.cta}
                      link={service.cta === "apply" ? DASHBOARD_URL : "#"}
                  />
                ))}
              </div>
            ) : (
              <div className="services-grid">
                {complianceServices.map((service, index) => (
                  <ServiceCard
                    key={`compliance-${index}`}
                    title={service.title}
                    desc={service.desc}
                    icon={service.icon}
                    features={service.features}
                    cta="learn"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="loans-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Need Help Choosing the Right Loan?</h2>
            <p>Our experts are here to guide you through the process and find the perfect solution for your needs.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">Talk to an Expert</Link>
              <Link to="/calculator" className="btn btn-secondary">Calculate EMI</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Loans;
